import React, { useState, PropsWithChildren, useEffect, useMemo } from 'react';
import {
    MappedComponentProperties,
} from '@adobe/aem-react-editable-components';
import SessionStorage from '../../utils/SessionStorage';
import { transformHTMLParserLinks } from '../../utils/rewriteLink/rewriteLink';
import './ConfirmationPageHero.css';
import { ICard } from '../../types/shared/Card';
import { ICardContent } from '../ConfirmationAdModule/components/CardDesignModal';
import {
    AcquisitionFlow,
    getAcquisitionFlow,
    getAppData,
    showCardTooltip,
} from '../../utils/acquisition-utils';
import { useWidthContext } from '../../providers/Width';
import { isAuthorMode, wrapTrademarkSuperscript } from '../../utils/cob-utils';
import { PathUtils } from '@adobe/aem-spa-page-model-manager';
import { useTimeBasedRedirect } from '../../hooks/useTimeBasedRedirect';
import { Row, Col } from '../../importCL';
import Approved from './variants/Approved';
import Pending from './variants/Pending';
import ApprovedPendingFunding from './variants/ApprovedPendingFunding';
import { AcquisitionsLoader } from '../AcquisitionsLoader/AcquisitionsLoader';

interface IConfirmationPageHeroProps extends MappedComponentProperties {
    version?: 'approved' | 'pending' | 'approvedPendingFunding';
    headline?: string;
    subHeadline?: string;
    iconTextDesktop?: string;
    iconDesktop?: string;
    iconName?: string;
    iconDesktopAlt?: string;
    iconMobile?: string;
    iconTextMobile?: string;
    iconMobileName?: string;
    iconAltMobile?: string;
    pushdownErrorCode?: string;
    sessionExpirationEnabled?: boolean;
    cardName?: string;
    fundingWarning?: string;
}
interface CreditBureauData {
    IsScoreBasedeDecline: boolean;
    CreditBureauContactInformation: any;
    CreditBureauName: any;
    CreditBureauAddress: any;
    CreditBureauPhone: any;
    CreditBureauWebAddress: any;
    CreditScore: number;
    IsDecisionFromCrsScore: boolean;
    LowCreditScore: number;
    HighCreditScore: number;
    CreditScoreDate: string;
    BureauFactorReasons: any[];
    BureauFactorCodes: any[];
    Errors: any[];
}

export interface IAppApprovedData {
    CardOffer?: string;
    Name?: string;
    ReservationNumber?: string;
    TransactionDateTime?: string;
    HasRBPNotification?: boolean;
    CreditBureauData?: CreditBureauData;
    CreditLine?: string;
    LetterName?: any;
    AvailableCredit?: string;
    IsRealTimeApplication?: boolean;
    InstantIssuanceOffer?: number;
    AdditionalInformation?: AdditionalInformation;
    parties?: any;
    card?: any;
    application?: any;
}

interface CreditBureauData {
    IsScoreBasedeDecline: boolean;
    CreditBureauContactInformation: any;
    CreditBureauName: any;
    CreditBureauAddress: any;
    CreditBureauPhone: any;
    CreditBureauWebAddress: any;
    CreditScore: number;
    IsDecisionFromCrsScore: boolean;
    LowCreditScore: number;
    HighCreditScore: number;
    CreditScoreDate: string;
    BureauFactorReasons: any[];
    BureauFactorCodes: any[];
    Errors: any[];
}

interface AdditionalInformation {
    SelectedProgramPlasticCode: string;
    Cards: ICard[];
    AuthorizedUserFee: number;
    ResponseStatusCode: number;
    IsBalanceTransferEligible: boolean;
    IsBalanceTransferSelected: boolean;
    IsCreditProtectionEligible: boolean;
    IsCreditProtectionSelected: boolean;
    IsAuthorizedUserEligible: boolean;
    IsAuthorizedUserSelected: boolean;
}

export interface Benefit {
    benefitTextContentLeft: string;
    benefitTextContentRight: string;
}

export interface AnnualFeeMessages {
    scenarioOne: string;
    scenarioTwo: string;
    scenarioThree: string;
}

interface ICardDesign {
    keyValues0_imagePath: string;
    keyValues0_cardId: string;
    keyValues0_altText: string;
    keyValues0_tooltiptext: string;
}
interface IPageData {
    firstName: string;
    creditLine: number;
    smCreditLine: boolean;
    productName: string;
    applicationCode: string;
}

export const ConfirmationPageHeroEditConfig = {
    emptyLabel: 'Confirmation Page Hero',
    isEmpty: (props: PropsWithChildren<IConfirmationPageHeroProps>) => {
        return !props || !props.headline;
    },
};

const ConfirmationPageHero = (
    props: PropsWithChildren<IConfirmationPageHeroProps>,
): JSX.Element => {
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const processingSteps = ['Finalizing your application', 'One moment'];
    const [subHeadline, setSubHeadline] = useState(props.subHeadline);
    const [selectedCard, setSelectedCard] = useState<ICardContent>({
        path: '',
        altText: '',
        fee: 0,
        name: '',
        cardID: '',
        isPremium: false,
    });
    const [pageData, setPageData] = useState<IPageData>({
        firstName: '',
        creditLine: 0,
        smCreditLine: false,
        productName: '',
        applicationCode: '',
    });


    let approvedData: any = {};
    const { width } = useWidthContext();
    const printCode = {
        icon: width > 767 ? props.iconDesktop : props.iconMobile,
        alt: width > 767 ? props.iconDesktopAlt : props.iconAltMobile,
        text: width > 767 ? props.iconTextDesktop : props.iconTextMobile,
    };

    const cardInfo: any[] = SessionStorage.tryGetObject('cardInfo', []);

    useEffect(() => {
        const setProductCustomerInfo = async () => {
            const updatedInfo: IPageData = {} as IPageData;
            let productNameNoTrade = '';
            if (props.cardName) {
                try {
                    const cardData: {
                                cardName?: string;
                                tags?: string[];
                            }[] = JSON.parse(props.cardName);

                    const selectedCode = SessionStorage.tryGet('selectedProgramCode', '');
            
                    for (const param of cardData) {
                        if (param.tags?.includes(selectedCode)) {
                            productNameNoTrade = param.cardName ?? '';
                            updatedInfo.productName = wrapTrademarkSuperscript(productNameNoTrade);
                        }
                    }
                } catch (e) {
                    console.debug('Failed to retrieve card data.');
                }
            }
            const prequalFullAppData = await SessionStorage.tryGetCipher('prequalFullAppData', null);
            if (getAcquisitionFlow() === AcquisitionFlow.PHA || (getAcquisitionFlow() === AcquisitionFlow.PQ && SessionStorage.verify('flowApplicationData'))) {
                approvedData = await SessionStorage.tryGetObjectOrCipher('flowApplicationData', {});
                updatedInfo.firstName = approvedData?.parties?.[0]?.firstName ?? '';
                updatedInfo.creditLine = parseInt(approvedData?.funding?.amountToFund ?? 0);
                updatedInfo.smCreditLine = updatedInfo.creditLine < 500;
                if (productNameNoTrade === '') {
                    productNameNoTrade = approvedData?.card?.name ?? '';
                    updatedInfo.productName = wrapTrademarkSuperscript(productNameNoTrade);
                }
                updatedInfo.applicationCode = approvedData?.application?.reservationNumber || approvedData?.reservationNumber || approvedData?.ReservationNumber || '';
            } else if (getAcquisitionFlow() === AcquisitionFlow.PQ && prequalFullAppData?.IsSecuredCard) {
                const flowData = await SessionStorage.tryGetCipher('flowData', {});
                if (SessionStorage.verify('app-approved-data')) {
                    approvedData = await SessionStorage.getCipher('app-approved-data', true);
                } else if (SessionStorage.verify('app-pending-data')) {
                    approvedData = await SessionStorage.getCipher('app-pending-data', true);
                }

                updatedInfo.firstName = flowData?.FirstName || '';
                updatedInfo.creditLine = prequalFullAppData?.FundingInformation ? 
                    parseInt(prequalFullAppData?.FundingInformation?.AmountToFund ?? 0) :
                    parseInt(prequalFullAppData?.DebitFundingInformation?.AmountToFund ?? 0);
                updatedInfo.smCreditLine = (updatedInfo.creditLine as number) < 500.0;
                if (productNameNoTrade === '') {
                    productNameNoTrade = approvedData?.CardOffer || '';
                    updatedInfo.productName = wrapTrademarkSuperscript(productNameNoTrade);
                }
                updatedInfo.applicationCode = approvedData?.ReservationNumber || '';
            } else {
                const flowData = await SessionStorage.tryGetCipher('flowData', {});

                if (SessionStorage.verify('app-approved-data')) {
                    approvedData = await SessionStorage.getCipher('app-approved-data', true);
                } else if (SessionStorage.verify('app-pending-data')) {
                    approvedData = await SessionStorage.getCipher('app-pending-data', true);
                }

                updatedInfo.firstName = flowData?.FirstName || '';
                updatedInfo.creditLine = approvedData?.CreditLine
                    ? parseInt(approvedData?.CreditLine?.replace(/[^\d.-]/g, ''))
                    : 0;
                updatedInfo.smCreditLine = (updatedInfo.creditLine as number) < 500.0;
                if (productNameNoTrade === '') {
                    productNameNoTrade = approvedData?.CardOffer || '';
                    updatedInfo.productName = wrapTrademarkSuperscript(productNameNoTrade);
                }
                updatedInfo.applicationCode = approvedData?.ReservationNumber || '';
            }
            setPageData(updatedInfo);
        };
        setProductCustomerInfo();
    }, []);


    useTimeBasedRedirect({
        hours: 24,
        timestampKey: 'confirmation-page-timestamp',
        pushdownError: props.pushdownErrorCode,
        enabled: !!props.sessionExpirationEnabled,
    });

    useEffect(() => {
        if (
            props.version !== 'pending' &&
            pageData &&
            !pageData.smCreditLine &&
            pageData.creditLine &&
            !subHeadline?.includes(' with a credit line of $')
        ) {
            setSubHeadline(
                subHeadline + ' with a credit line of $' + pageData.creditLine.toLocaleString(),
            );
        }
    }, [subHeadline, pageData]);

    useEffect(() => {
        if (isAuthorMode()) {
            return;
        }
        
        const data = JSON.parse(PathUtils.getMetaPropertyValue('cardDesignContent') as string);

        getAppData().then(async (appData) => {

            // new funding flow
            if (getAcquisitionFlow() === AcquisitionFlow.PQ && SessionStorage.verify('flowApplicationData')) {
                const flowApplicationData = await SessionStorage.tryGetObjectOrCipher('flowApplicationData', {});
                const cardDesigns = data.cardArts;
                const tooltipMapping = data.tooltipMapping;
                const programCode = SessionStorage.tryGet('selectedProgramCode', '');
                const selectedCard = flowApplicationData?.card?.keyCardFeatures?.cardVariations?.[0] ?? cardInfo[0];
                const cardCode = selectedCard?.cardMaterialId ?? selectedCard.Code;
                const matchingCardDesignRawData = cardDesigns.find((card) => JSON.parse(card).keyValues0_cardId === cardCode);
                
                if (matchingCardDesignRawData) {
                    const parsedCardDesignData = JSON.parse(matchingCardDesignRawData);
                    setSelectedCard({
                        path: parsedCardDesignData.keyValues0_imagePath,
                        cardID: cardCode,
                        isPremium: !!selectedCard.IsPremium,
                        fee: selectedCard.PremiumFee,
                        name: selectedCard.CardName,
                        tooltipText: showCardTooltip(
                            tooltipMapping,
                            selectedCard.Code,
                            programCode,
                        )
                            ? parsedCardDesignData.keyValues0_tooltiptext
                            : undefined,
                        altText: selectedCard.CardName,
                    });
                }
                return;
            }
    
            let cardId;
            if (getAcquisitionFlow() === AcquisitionFlow.PA) {
                cardId = parseInt(appData.SelectedProgramPlasticCode);
            } else {
                cardId = parseInt(appData.SelectedCardId);
            }
    
            // cardMaterialId
            const chosenCard = cardInfo.find(
                (card: any) => card.ProgramPlasticCode === cardId,
            );
            const cardDesigns = data.cardArts;
            const tooltipMapping = data.tooltipMapping;
            const programCode = SessionStorage.tryGet('selectedProgramCode', '');
    
            cardDesigns.forEach((card: string) => {
                const cardJson: ICardDesign = JSON.parse(card);
                if (cardJson.keyValues0_cardId === chosenCard?.Code) {
                    setSelectedCard({
                        path: cardJson.keyValues0_imagePath,
                        cardID: chosenCard?.Code,
                        isPremium: !!chosenCard?.IsPremium,
                        fee: chosenCard.PremiumFee,
                        name: chosenCard.CardName,
                        tooltipText: showCardTooltip(
                            tooltipMapping,
                            chosenCard?.Code,
                            programCode,
                        )
                            ? cardJson.keyValues0_tooltiptext
                            : undefined,
                        altText: chosenCard.CardName,
                    });
                }
            });
        });
    }, []);

    useEffect(() => {
        const handleUpdateCardDesign = async (e: CustomEvent<ICardContent>) => {
            setSelectedCard({
                ...e.detail,
            });
            if (getAcquisitionFlow() === AcquisitionFlow.PA) {
                const card = cardInfo.find(
                    (card: any) => card.PlasticId === e.detail.cardID,
                );

                if (card) {
                    const cardCode = card.Code;
                    const preApprovalFullAppData = await SessionStorage.getCipher('preApprovalFullAppData', true);
                    const additionalInformation =
                        approvedData.AdditionalInformation as AdditionalInformation;
                    additionalInformation.SelectedProgramPlasticCode = cardCode;
                    preApprovalFullAppData.SelectedProgramPlasticCode = card.ProgramPlasticCode;
                    await SessionStorage.setCipher('preApprovalFullAppData', preApprovalFullAppData);
                    await SessionStorage.setCipher('app-approved-data', approvedData);
                }
            } else if (getAcquisitionFlow() === AcquisitionFlow.PHA || (getAcquisitionFlow() === AcquisitionFlow.PQ && SessionStorage.verify('flowApplicationData'))) {
                const card = cardInfo.find(
                    (card: any) => card.PlasticId === e.detail.cardID,
                );

                if (card) {
                    const cardIndex = card.ProgramPlasticCode;
                    const flowApplicationData = await SessionStorage.tryGetObjectOrCipher('flowApplicationData', {});
                    flowApplicationData.SelectedCardId = cardIndex;
                    flowApplicationData.SelectedProgramPlasticCode = cardIndex;
                    await SessionStorage.setCipherWithCurrentScope(
                        'flowApplicationData',
                        flowApplicationData,
                    );
                }
            } else {
                const card = cardInfo.find(
                    (card: any) => card.PlasticId === e.detail.cardID,
                );

                if (card) {
                    const cardIndex = card.ProgramPlasticCode;
                    const prequalFullAppData =
                        await SessionStorage.getCipher('prequalFullAppData', true);
                    prequalFullAppData.SelectedCardId = cardIndex;
                    await SessionStorage.setCipher(
                        'prequalFullAppData',
                        prequalFullAppData,
                    );
                }
            }
        };

        const eventHandler = ((
            e: CustomEvent<ICardContent>,
        ) => handleUpdateCardDesign(e)) as EventListener;

        window.addEventListener('UpdateCardDesign', eventHandler);

        return () => {
            window.removeEventListener('UpdateCardDesign', eventHandler);
        };
    }, []);

    const retrieveConfirmationPageHeroVariant = () => {
        switch (props.version) {
            case 'approved': {
                return <Approved
                    selectedCard={selectedCard}
                    applicationCode={pageData.applicationCode}
                    smCreditLine={pageData.smCreditLine}
                    creditLine={pageData.creditLine}
                />;
            }
            case 'pending': {
                return <Pending
                    applicationCode={pageData.applicationCode}
                    printCode={printCode}/>;
            }
            default: {
                return <ApprovedPendingFunding
                    fundingWarning={props.fundingWarning}
                    iconDesktop={props.iconDesktop}
                    selectedCard={selectedCard}
                    applicationCode={pageData.applicationCode}/>;
            }
        }
    };

    const headline = useMemo(
        () => props.headline?.replace('{{firstName}}', pageData.firstName).replace('{{cardOffer}}', pageData.productName) ?? ''
        , [pageData]);

    const capitalizedVersion = props.version?.charAt(0).toUpperCase() + props.version?.slice(1);

    /**
     * Everything relating to the Acquisitions Loader can likely be removed in the future.
     * It was only added because our servers are inconsistent and sometimes throw incorrect
     * values before everything is loaded in.
     * @see DX-9803
     */
    useEffect(() => {
        const timer = setTimeout(() => {
            setIsLoading(false);
        }, 5000);

        return () => {
            clearTimeout(timer);
        };
    }, []);


    if (isLoading) {
        return (
            <div className='ConfirmationPageHero__loader'>
                <AcquisitionsLoader processingSteps={processingSteps} />
            </div>
        );
    }

    return (
        <div id='confirmation-page-hero' className={`ConfirmationPageHero ${props.version === 'approvedPendingFunding' ? 'flex flex-col items-center' : ''} ${capitalizedVersion ? 'ConfirmationPageHero' + capitalizedVersion : ''}`}>
            <Row className='m-0'>
                <Col
                    className={`ConfirmationPageHero__heading !px-0 ${props.version === 'approvedPendingFunding' ? 'max-w-[41rem]' : ''}`}
                    lg={12}
                    md={12}
                >                      
                    {transformHTMLParserLinks(wrapTrademarkSuperscript(headline))}
                </Col>
            </Row>
            <Row>
                <Col
                    className={`${subHeadline ? 'ConfirmationPageHero__subHeading' : ''}`}
                    lg={12}
                    md={12}
                >
                    {transformHTMLParserLinks(wrapTrademarkSuperscript(subHeadline?.replace('{{productName}}', pageData.productName) ?? ''))}
                </Col>
            </Row>
            {retrieveConfirmationPageHeroVariant()}
        </div>
    );
};

export default ConfirmationPageHero;
